import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import { AlertTriangle, CheckCircle, Mic, Volume2 } from 'react-feather';

import { UIModal } from '../ui-elements/UIModal';
import { UIButton } from '../ui-elements/UIButton';
import {
  AudioIssue,
  ISSUE_MESSAGES,
  LevelMeter,
  TWILIO_INCOMING_SOUND_URL,
  checkMicrophone,
  createLevelMeter,
  isLevelMeterSupported,
  stopStream,
} from '@/lib/audio-test';

interface TestAudioModalProps {
  onClose: () => void;
  soundUrl?: string;
}

type MicState = 'checking' | 'ready' | 'unavailable';
type SoundState = 'idle' | 'loading' | 'playing' | 'error';

// How long to wait for any mic signal before warning nothing is picked up.
const NO_SIGNAL_AFTER_MS = 5000;
// Scaled level (see createLevelMeter's gain) above ambient noise, easily reached by speech.
const NO_SIGNAL_THRESHOLD = 0.15;
const METER_INTERVAL_MS = 50;
// Give up on a stalled ringtone download rather than leaving the button disabled.
const SOUND_LOAD_TIMEOUT_MS = 10000;

// Lets a user verify they can hear Twilio's incoming-call ringtone and that their microphone is picking up sound.
export const TestAudioModal = ({ onClose, soundUrl = TWILIO_INCOMING_SOUND_URL }: TestAudioModalProps) => {
  const [micState, setMicState] = useState<MicState>('checking');
  const [issues, setIssues] = useState<AudioIssue[]>([]);
  const [level, setLevel] = useState(0);
  const [signalDetected, setSignalDetected] = useState(false);
  const [noSignal, setNoSignal] = useState(false);
  const [meterUnavailable, setMeterUnavailable] = useState(false);
  const [microphoneLabel, setMicrophoneLabel] = useState<string | null>(null);
  const [soundState, setSoundState] = useState<SoundState>('idle');
  const [soundError, setSoundError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Set when a playback attempt failed; the element then holds a permanent
  // MediaError (or a dead download), so the next attempt starts from a fresh one.
  const audioFailedRef = useRef(false);
  const audioListenersRef = useRef<{ [K in 'ended' | 'playing' | 'error']: EventListener } | null>(null);
  const loadTimeoutRef = useRef<number | undefined>(undefined);

  // Microphone check + level meter lifecycle. Re-runs when the user retries.
  useEffect(() => {
    let cancelled = false;
    let stream: MediaStream | null = null;
    let meter: LevelMeter | null = null;
    let interval: number | undefined;
    let noSignalTimer: number | undefined;
    let seenSignal = false;

    setMicState('checking');
    setIssues([]);
    setLevel(0);
    setSignalDetected(false);
    setNoSignal(false);
    setMeterUnavailable(false);
    setMicrophoneLabel(null);

    checkMicrophone().then((result) => {
      if (cancelled) {
        stopStream(result.stream);
        return;
      }
      stream = result.stream;
      setIssues(result.issues);

      if (!stream) {
        setMicState('unavailable');
        return;
      }

      const track = stream.getAudioTracks()[0];
      setMicrophoneLabel(track?.label || result.devices.inputs[0]?.label || null);
      setMicState('ready');

      // No meter means we can't detect signal, so say so instead of a bar stuck at zero.
      if (!isLevelMeterSupported()) {
        setMeterUnavailable(true);
        return;
      }
      try {
        meter = createLevelMeter(stream);
      } catch (error) {
        console.error(error);
        setMeterUnavailable(true);
        return;
      }

      interval = window.setInterval(() => {
        const value = meter!.read();
        setLevel(value);
        if (value > NO_SIGNAL_THRESHOLD && !seenSignal) {
          seenSignal = true;
          setSignalDetected(true);
          setNoSignal(false);
          window.clearTimeout(noSignalTimer);
        }
      }, METER_INTERVAL_MS);

      noSignalTimer = window.setTimeout(() => {
        if (!seenSignal) setNoSignal(true);
      }, NO_SIGNAL_AFTER_MS);
    });

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.clearTimeout(noSignalTimer);
      meter?.stop();
      stopStream(stream);
    };
  }, [retryCount]);

  // Detaches listeners and releases the media resource, so the element can be
  // discarded without its teardown firing the error handler.
  const releaseAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    const listeners = audioListenersRef.current;
    if (listeners) {
      audio.removeEventListener('ended', listeners.ended);
      audio.removeEventListener('playing', listeners.playing);
      audio.removeEventListener('error', listeners.error);
    }
    // src = '' resolves to the document URL in Chrome and fires a spurious error; removeAttribute avoids that.
    audio.removeAttribute('src');
    audio.load();
    audioRef.current = null;
    audioListenersRef.current = null;
  }, []);

  // Stop the ringtone when the modal goes away.
  useEffect(() => () => {
    window.clearTimeout(loadTimeoutRef.current);
    releaseAudio();
  }, [releaseAudio]);

  const stopSound = useCallback(() => {
    window.clearTimeout(loadTimeoutRef.current);
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setSoundState('idle');
  }, []);

  const playSound = useCallback(async () => {
    setSoundError(null);
    // A failed element keeps its MediaError forever: play() would reject with
    // NotSupportedError (misdiagnosed as a speaker problem) or silently never
    // re-run resource selection. Start the retry from a fresh element instead.
    if (audioFailedRef.current) {
      releaseAudio();
      audioFailedRef.current = false;
    }
    if (!audioRef.current) {
      const audio = new Audio(soundUrl);
      audio.preload = 'auto';
      const listeners = {
        ended: () => setSoundState('idle'),
        playing: () => {
          window.clearTimeout(loadTimeoutRef.current);
          setSoundState('playing');
        },
        error: () => {
          window.clearTimeout(loadTimeoutRef.current);
          audioFailedRef.current = true;
          setSoundState('error');
          setSoundError('The test sound could not be loaded. Check your internet connection, and make sure sdk.twilio.com is not blocked by a firewall or extension.');
        },
      };
      audio.addEventListener('ended', listeners.ended);
      audio.addEventListener('playing', listeners.playing);
      audio.addEventListener('error', listeners.error);
      audioListenersRef.current = listeners;
      audioRef.current = audio;
    }
    const audio = audioRef.current;
    audio.currentTime = 0;
    setSoundState('loading');
    window.clearTimeout(loadTimeoutRef.current);
    loadTimeoutRef.current = window.setTimeout(() => {
      audioFailedRef.current = true; // the stalled download is not worth resuming
      audio.pause();
      setSoundState('error');
      setSoundError('The test sound is taking too long to load. Check your internet connection, and make sure sdk.twilio.com is not blocked by a firewall or extension.');
    }, SOUND_LOAD_TIMEOUT_MS);
    try {
      await audio.play();
    } catch (error) {
      window.clearTimeout(loadTimeoutRef.current);
      const errorName = (error as { name?: string })?.name;
      // pause() from the timeout, Stop, or unmount rejects with AbortError; that path already set the right state.
      if (errorName === 'AbortError') return;
      console.error(error);
      // When the error listener already fired for this attempt it has set a more
      // specific diagnosis (bad download vs. this generic one); keep it.
      const alreadyDiagnosed = audioFailedRef.current;
      audioFailedRef.current = true;
      setSoundState('error');
      if (!alreadyDiagnosed) {
        setSoundError(errorName === 'NotAllowedError'
          ? 'The browser blocked audio playback. Please click the button again, and check that sound is not muted for this site.'
          : 'The test sound could not be played. Check your speakers or headset and try again.');
      }
    }
  }, [soundUrl, releaseAudio]);

  const isPlaying = soundState === 'playing';
  const allIssues: AudioIssue[] = [
    ...issues,
    ...(noSignal ? [{ code: 'no_signal' as const, message: ISSUE_MESSAGES.no_signal }] : []),
  ];

  const modal = (
    <UIModal
      title="Test Audio"
      onClose={onClose}
      width={520}
      footer={(
        <UIButton type="button" $color="green" onClick={onClose}>Done</UIButton>
      )}
    >
      <Section>
        <SectionTitle><Volume2 size={16} /> Speakers</SectionTitle>
        <p>Play the sound you will hear when a call comes in. If the indicator animates but you hear nothing, check your device volume, mute switch and selected output device.</p>
        <Row>
          <UIButton
            type="button"
            $color={isPlaying ? 'red' : 'brightgreen'}
            onClick={isPlaying ? stopSound : playSound}
            disabled={soundState === 'loading'}
            data-testid="test-sound-button"
          >
            {soundState === 'loading' ? 'Loading…' : (isPlaying ? 'Stop' : 'Play Test Sound')}
          </UIButton>
          <PlayingIndicator
            $active={isPlaying}
            role="status"
            aria-live="polite"
            aria-label={isPlaying ? 'Sound is playing' : 'Sound is not playing'}
            data-testid="playing-indicator"
          >
            <Bar $active={isPlaying} $delay={0} />
            <Bar $active={isPlaying} $delay={0.15} />
            <Bar $active={isPlaying} $delay={0.3} />
            <Bar $active={isPlaying} $delay={0.45} />
            <Bar $active={isPlaying} $delay={0.6} />
            <IndicatorLabel>{isPlaying ? 'Playing…' : 'Not playing'}</IndicatorLabel>
          </PlayingIndicator>
        </Row>
        {soundError && <IssueLine role="alert"><AlertTriangle size={16} /> {soundError}</IssueLine>}
      </Section>

      <Section>
        <SectionTitle><Mic size={16} /> Microphone</SectionTitle>
        {micState === 'checking' && <p>Checking your microphone… If your browser asks for permission, please allow it.</p>}
        {micState === 'ready' && (
          <>
            {meterUnavailable ? (
              <p data-testid="meter-unavailable">Your microphone is connected and permission was granted, but this browser cannot measure the input level, so the signal indicator is unavailable.</p>
            ) : (
              <p>Say something. The bar should move as you speak.</p>
            )}
            {!meterUnavailable && (
            <MeterRow>
              <Meter role="meter" aria-label="Microphone signal strength" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(level * 100)}>
                <MeterFill
                  style={{
                    width: `${Math.round(level * 100)}%`,
                    backgroundSize: level > 0 ? `${Math.round(100 / level)}% 100%` : '100% 100%',
                  }}
                />
              </Meter>
              <MeterStatus $ok={signalDetected} data-testid="mic-status">
                {signalDetected ? <><CheckCircle size={14} /> Signal detected</> : 'Listening…'}
              </MeterStatus>
            </MeterRow>
            )}
            {microphoneLabel && <DeviceLabel>Using: {microphoneLabel}</DeviceLabel>}
          </>
        )}
        {micState === 'unavailable' && (
          <Row>
            <UIButton type="button" $color="transparentoutlined" onClick={() => setRetryCount((c) => c + 1)}>
              Try Again
            </UIButton>
          </Row>
        )}
      </Section>

      {allIssues.length > 0 && (
        <Section>
          <SectionTitle><AlertTriangle size={16} color="#d22600" /> Issues Found</SectionTitle>
          <IssueList data-testid="issues">
            {allIssues.map((item) => (
              <IssueLine key={item.code} as="li" role="alert">{item.message}</IssueLine>
            ))}
          </IssueList>
        </Section>
      )}
      {allIssues.length === 0 && micState === 'ready' && (signalDetected || meterUnavailable) && (
        <AllGood data-testid="all-good"><CheckCircle size={16} /> {meterUnavailable ? 'No issues detected. Your microphone is connected.' : 'No issues detected. Your microphone is working.'}</AllGood>
      )}
    </UIModal>
  );

  return typeof document !== 'undefined' ? createPortal(modal, document.body) : modal;
};

export default TestAudioModal;

const Section = styled.div`
  & + & { margin-top: 20px; }
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 6px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 700 !important;
  color: #171717;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const bounce = keyframes`
  0%, 100% { transform: scaleY(0.3); }
  50% { transform: scaleY(1); }
`;

// styled-components v6: declare props without the `$` prefix, pass/read with it.
const PlayingIndicator = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 3px;
  height: 28px;
  padding: 0 12px;
  border-radius: 14px;
  background: ${({ $active }) => ($active ? '#d1f2dc' : '#f6f6f6')};
  transition: background 0.2s;
`;

const Bar = styled.span<{ active: boolean; delay: number }>`
  display: inline-block;
  width: 3px;
  height: 16px;
  border-radius: 2px;
  background: ${({ $active }) => ($active ? '#19be51' : '#bdbdbd')};
  transform: scaleY(0.3);
  transform-origin: center;
  ${({ $active, $delay }) => $active && css`
    animation: ${bounce} 0.8s ease-in-out ${$delay}s infinite;
  `}
`;

const IndicatorLabel = styled.span`
  margin-left: 8px;
  font-size: 12px;
  font-weight: 600 !important;
  color: #505050;
`;

const MeterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
`;

const Meter = styled.div`
  flex: 1;
  height: 12px;
  border-radius: 6px;
  background: #ececec;
  overflow: hidden;
`;

// width/background-size use the style prop, not interpolation, to avoid a new CSS class every level update (20x/sec).
const MeterFill = styled.div`
  height: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #19be51 0%, #19be51 70%, #f5a623 90%, #d22600 100%);
  transition: width 0.06s linear;
`;

const MeterStatus = styled.span<{ ok: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 120px;
  font-size: 12px;
  font-weight: 600 !important;
  color: ${({ $ok }) => ($ok ? '#1e705a' : '#808080')};
`;

const DeviceLabel = styled.p`
  margin-top: 6px !important;
  font-size: 12px;
  color: #808080;
`;

const IssueList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const IssueLine = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px !important;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(210, 38, 0, 0.08);
  color: #b92100;
  font-weight: 600 !important;
  line-height: 20px;

  svg { flex-shrink: 0; margin-top: 2px; }
`;

const AllGood = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px !important;
  padding: 10px 12px;
  border-radius: 8px;
  background: #d1f2dc;
  color: #1e705a;
  font-weight: 600 !important;
`;
